import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-tap voice dictation button. Starts recording on first click, stops on
 * second click, POSTs to /api/ai/transcribe, then invokes onResult(text).
 *
 * Requires microphone permission; falls back to a toast error if unavailable.
 */
export default function VoiceDictateButton({ onResult, testid, label = "Dictate", size = "sm" }) {
  const [state, setState] = useState("idle"); // idle | recording | sending
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const cleanup = useCallback(() => {
    try { mediaRef.current?.stop(); } catch { /* already stopped */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* already released */ }
    mediaRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = async () => {
    if (state !== "idle") return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      mediaRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        cleanup();
        if (blob.size < 500) {
          toast.error("Too short — tap & hold for a second, then speak.");
          setState("idle");
          setElapsed(0);
          return;
        }
        setState("sending");
        const form = new FormData();
        form.append("audio", blob, "clip.webm");
        try {
          const res = await axios.post(`${API}/ai/transcribe`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          const text = (res.data?.text || "").trim();
          if (!text) {
            toast.warning("Heard nothing — try again closer to the mic.");
          } else {
            onResult?.(text);
            toast.success("Transcribed ✨");
          }
        } catch (e) {
          toast.error(`Transcription failed: ${e.response?.data?.detail || e.message}`);
        } finally {
          setState("idle");
          setElapsed(0);
        }
      };
      rec.start();
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((x) => x + 1), 1000);
      // Hard stop at 60s
      setTimeout(() => { try { if (mediaRef.current?.state === "recording") mediaRef.current.stop(); } catch { /* already stopped by user */ } }, 60000);
    } catch (e) {
      toast.error(`Mic permission denied: ${e.message || e}`);
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    try { mediaRef.current?.stop(); } catch { /* already stopped */ }
  };

  const onClick = () => (state === "recording" ? stop() : start());

  const base = "inline-flex items-center gap-1.5 font-semibold rounded-full transition border";
  const sizeCls = size === "lg"
    ? "text-[13px] px-3 h-9"
    : "text-[11px] px-2.5 h-6";

  if (state === "recording") {
    return (
      <button type="button" onClick={onClick} data-testid={testid}
        className={`${base} ${sizeCls} bg-[#c05746] border-[#8a2e1a] text-white animate-pulse`}>
        <Square className="w-3 h-3 fill-white" /> Stop · {elapsed}s
      </button>
    );
  }
  if (state === "sending") {
    return (
      <button type="button" disabled data-testid={testid}
        className={`${base} ${sizeCls} bg-[#E7F1FB] border-[#B7CADA] text-[#0A3D79] opacity-80`}>
        <Loader2 className="w-3 h-3 animate-spin" /> Transcribing…
      </button>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid} title="Dictate into this field"
      className={`${base} ${sizeCls} bg-[#E7F1FB] border-[#B7CADA] text-[#0A3D79] hover:bg-[#d9e6df]`}>
      <Mic className="w-3 h-3" /> {label}
    </button>
  );
}
